// Consult-note SSE transport the package cannot reach: region, auth, headers,
// the open handler, and two fetch-time reads. Required members, fail-loud —
// an absent one would open a stream against the wrong host or skip the abort.

export interface ConsultNoteStreamConfig {
  /**
   * Region-resolved v2.2 origin, read per request. Capturing it at import
   * time would pin the wrong region for a later session. Heal:
   * `() => getDomain('v2-2')` in `src/utils/session-data-wiring.ts`.
   */
  resolveV2_2BaseUrl: () => string;
  /** Bearer lookup. Stays ahead of the abort check and the header build. */
  getAuthToken: () => Promise<string | null | undefined>;
  /** SSE headers. Bound after `signal.aborted`, never before the token read. */
  buildSseHeaders: (token: string | null | undefined) => Record<string, string>;
  /**
   * Shared non-OK / onOpen path. The package must not import the app logger
   * or the prompt-injection parser this wraps.
   */
  handleStreamOpen: (args: {
    response: Response;
    logPrefix: string;
    onOpen?: (response: Response) => Promise<void> | void;
    onError?: (error: unknown) => void;
    context?: Record<string, unknown>;
  }) => Promise<void>;
  /**
   * PostHog flag, read when the request body is built. A boolean captured at
   * wiring time would freeze the first evaluation for the process.
   */
  isTranscriptionServiceV2Enabled: () => boolean;
  /** Shortened-demo tag for the generation event. Absent means a real note. */
  getDemoModeForSession: (sessionId: string) => 'shortened' | undefined;
}

let config: ConsultNoteStreamConfig | null = null;

export const configureConsultNoteStream = (next: ConsultNoteStreamConfig): void => {
  config = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getConsultNoteStreamConfig = (): ConsultNoteStreamConfig => {
  if (!config) {
    throw new Error(
      '[session/data] consult-note stream not configured — app bootstrap must call configureConsultNoteStream() ' +
        '(see src/utils/session-data-wiring.ts); tests exercising the real stream must configure a test double first.'
    );
  }
  return config;
};
